import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import {
    Box,
    Card,
    Button,
    Container,
    Grid,
    Typography,
    FormControl,
    TextField,
    Snackbar,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Alert,
} from '@mui/material';
import { Title } from '../../components/Title';
import { CategoryData, getCategory, postCategory, putCategory, deleteCategory } from '../../data';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IToastState } from '../../components/ToastState';
import { accountService, UserRole } from '../../account.service';
import { Navigate } from 'react-router-dom';

interface IFormData {
    name: string;
    icon: string;
    description: string;
}

export const CategoryDetails = () => {
    const { categoryId } = useParams();
    const navigate = useNavigate();
    const account = accountService.getAccount();
    const [category, setCategory] = useState<CategoryData | null>(null);
    const [openDialog, setOpenDialog] = useState(false);

    const [toastState, setToastState] = useState<IToastState>({
        open: false,
        status: 'success',
        message: '',
    });

    useEffect(() => {
        let cancelled = false;
        const doGetCategory = async (categoryId: number) => {
            const fetchedCategory = await getCategory(categoryId);
            if (!cancelled) {
                setCategory(fetchedCategory);
            }
        };
        if (categoryId) {
            doGetCategory(Number(categoryId));
        } else {
            setCategory({
                categoryId: 0,
                name: '',
                icon: '',
                description: '',
                questionCount: 0,
            });
        }
        return () => {
            cancelled = true;
        };
    }, []);

    const { handleSubmit, control } = useForm<IFormData>();
    const onSubmit: SubmitHandler<IFormData> = async (data) => {
        let state: IToastState;

        if (category.categoryId === 0) {
            const result = await postCategory({
                name: data.name,
                icon: data.icon,
                description: data.description,
            });

            if (result) {
                state = { open: true, status: 'success', message: 'Category was successfully created' };
            } else {
                state = { open: true, status: 'error', message: 'Failed to create category' };
            }
        } else {
            const result = await putCategory(Number(categoryId), {
                name: data.name,
                icon: data.icon,
                description: data.description,
            });

            if (result) {
                state = { open: true, status: 'success', message: 'Category was successfully updated' };
            } else {
                state = { open: true, status: 'error', message: 'Failed to save category' };
            }
        }
        setToastState(state);
    };

    const handleCloseSnackbar = (event?: React.SyntheticEvent, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setToastState({ ...toastState, open: false });
    };

    const handleDelete = async () => {
        setOpenDialog(false);
        const result = await deleteCategory(Number(categoryId));

        if (!result) {
            let state: IToastState;
            state = { open: true, status: 'error', message: 'Failed to delete category' };
            setToastState(state);
        } else {
            navigate('/kb/categories');
        }
    };

    return (
        <>
            {(!account || account.role !== UserRole.Admin) && <Navigate to="/account/login" />}
            {category && <Title title={category.name} />}
            <Box
                sx={{
                    backgroundColor: 'background.default',
                    minHeight: '100%',
                    py: 8,
                }}
            >
                <Dialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{'Delete this category?'}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Deleting this category will make all its articles not assigned to a category. Do you want to
                            continue?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                        <Button onClick={handleDelete} autoFocus>
                            ОК
                        </Button>
                    </DialogActions>
                </Dialog>
                {category && (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Container maxWidth={false}>
                            <Snackbar
                                open={toastState.open}
                                autoHideDuration={6000}
                                onClose={handleCloseSnackbar}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            >
                                <Alert onClose={handleCloseSnackbar} severity={toastState.status}>
                                    {toastState.message}
                                </Alert>
                            </Snackbar>

                            <Grid container justifyContent="space-between" spacing={3}>
                                <Grid item lg={8} xl={10} xs={12}>
                                    <Typography color="textPrimary" variant="h5">
                                        {category.categoryId === 0 ? 'Create' : 'Edit'} category
                                    </Typography>
                                </Grid>
                                <Grid item lg={4} xl={2} xs={12}>
                                    <FormControl fullWidth>
                                        <Button color="primary" variant="contained" type="submit">
                                            Save
                                        </Button>
                                    </FormControl>
                                </Grid>
                            </Grid>
                            <Card sx={{ mt: 5 }}>
                                <Box sx={{ p: 3 }}>
                                    <Grid container justifyContent="space-between" spacing={5}>
                                        <Grid item xs={12}>
                                            <Controller
                                                name="name"
                                                control={control}
                                                defaultValue={category.name}
                                                rules={{
                                                    required: 'Name is required',
                                                    maxLength: {
                                                        value: 100,
                                                        message: 'Name cannot exceed 100 characters',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        label="Name"
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Controller
                                                name="icon"
                                                control={control}
                                                defaultValue={category.icon}
                                                rules={{
                                                    required: 'Icon is required',
                                                    maxLength: {
                                                        value: 100,
                                                        message: 'Icon cannot exceed 100 characters',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        label="Icon"
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Controller
                                                name="description"
                                                control={control}
                                                defaultValue={category.description}
                                                rules={{
                                                    required: 'Description is required',
                                                    maxLength: {
                                                        value: 250,
                                                        message: 'Description cannot exceed 250 characters',
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        label="Description"
                                                        error={!!error}
                                                        helperText={error ? error.message : null}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        {category.categoryId !== 0 && (
                                            <Grid item>
                                                <Button color="secondary" onClick={() => setOpenDialog(true)}>
                                                    Delete category
                                                </Button>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            </Card>
                        </Container>
                    </form>
                )}
            </Box>
        </>
    );
};
